import type { ArgumentsSchema, ArgumentsType, DataSourceAbstract, FieldADTValue, RecordLikeProtocol } from '@lighthouse/core'
import { format as formatUtils } from 'date-fns'
import { isNil } from 'rambda'
import { SolarDay, SolarTime } from 'tyme4ts'

import type { FieldConvertValueOption } from './helper'
import { DATE_FORMAT, fieldConvertValue } from './helper'
import { readBeijingBaseFormat, toShanghaiWall } from './shanghaiTime'

/**
 * 根据入参类型获取转换后的参数
 *
 * @param {string} str
 * @param {ArgumentsSchema['type']} type
 * @returns {*}
 */
export function transformStringByType(str: string | null, type: ArgumentsType) {
    switch (type) {
        case 'ARRAY': {
            return str ? str.split(',') : []
        }

        case 'BOOL': {
            return str === 'true'
        }

        case 'NUMBER': {
            const value = Number(str)
            return Number.isNaN(value) ? 0 : value
        }

        case 'DATE': {
            const value = Number(str)
            return Number.isNaN(value) ? Date.now().valueOf() : value
        }

        default: {
            return str ? str.toString() : ''
        }
    }
}

/**
 * 从路径获取页面参数
 *
 * @export
 * @param {(undefined | ArgumentsSchema[])} data
 * @param {string[]} arr
 * @returns {Record<string, ReturnType<(str: string, type: ArgumentsType) => any>>}
 */
export function getPagePathParams(data: undefined | ArgumentsSchema[], arr: string[]) {
    if (!data || data.length === 0) {
        return
    }

    const params: Record<string, ReturnType<typeof transformStringByType>> = {}
    for (const [index, item] of data.entries()) {
        const str = arr[index]
        const value = transformStringByType(str, item.innerType)
        params[item.id] = value
    }

    return params
}

/**
 * 从查询获取页面参数
 *
 * @param {(undefined | ArgumentsSchema[])} data
 * @param {Record<string, string>} obj
 * @returns {Record<string, ReturnType<(str: string, type: ArgumentsType) => any>>}
 */
export function getPageQueryParams(data: undefined | ArgumentsSchema[], obj: Record<string, string>) {
    if (!data || data.length === 0) {
        return
    }

    const params: Record<string, ReturnType<typeof transformStringByType>> = {}
    for (const item of data) {
        const str = obj[item.id]
        const value = transformStringByType(str, item.innerType)
        params[item.id] = value
    }

    return params
}

/**
 * 解析数据源字段为纯文本
 * @param dataSource 数据源
 * @param fieldId 字段id
 * @param record 记录
 * @returns 字符串值
 */
export function parseField2TextValue(
    dataSource: DataSourceAbstract,
    fieldId: string,
    record: RecordLikeProtocol,
    option?: FieldConvertValueOption
) {
    const { schema } = dataSource
    const field = schema?.[fieldId]
    if (!field) {
        return ''
    }
    const value = record.content?.[fieldId]?.value

    if (isNil(value) && field.type !== 'checkbox') {
        return ''
    }

    const fieldValue = { ...field, value } as FieldADTValue
    return variableFieldValueConvert2string(fieldValue, option)
}

/** 解析变量时间类型的值 */
export function parseVariableDate(timestamp: number | Date, format: string): string {
    const beijingBase = readBeijingBaseFormat(format)
    if (beijingBase) {
        const instant = timestamp instanceof Date ? timestamp.getTime() : timestamp
        const wall = toShanghaiWall(instant)
        return `${formatUtils(wall, beijingBase)} (GMT+8)`
    }

    const date = new Date(timestamp)
    switch (format) {
        case 'lunar-y': {
            const lunarDay = SolarDay.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate()).getLunarDay()
            return `${lunarDay.getYearSixtyCycle().getName()}年`
        }

        case 'lunar-md': {
            const lunarDay = SolarDay.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate()).getLunarDay()
            return `${lunarDay.getMonth().getName().replace('十二月', '腊月')}${lunarDay.getName()}`
        }

        case 'lunar-h': {
            const lunarHour = SolarTime.fromYmdHms(
                date.getFullYear(),
                date.getMonth() + 1,
                date.getDate(),
                date.getHours(),
                date.getMinutes(),
                date.getSeconds()
            ).getLunarHour()
            return lunarHour.getName()
        }

        default: {
            return formatUtils(timestamp, format)
        }
    }
}
/** 变量字段值转换为纯文本 */
function variableFieldValueConvert2string(fieldValue: FieldADTValue, option?: FieldConvertValueOption): string {
    if (fieldValue.type === 'date' && fieldValue.innerType === 'DATE') {
        const {
            date: { format = DATE_FORMAT },
            value
        } = fieldValue

        const mergeFormat = option?.format || format

        if (Array.isArray(value)) {
            return value
                .map(item => (item ? parseVariableDate(Number(item), mergeFormat) : ''))
                .filter(Boolean)
                .join(',')
        }
        return value ? parseVariableDate(Number(value), mergeFormat) : ''
    }

    return fieldConvertValue(fieldValue, option)
}
